import { useCallback, useMemo, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { type ListingFilters, type SortOption } from '@/types/filters'

export function useFilters() {
  const [searchParams, setSearchParams] = useSearchParams()

  // Ref tracks the latest params so sequential setFilter calls in the same
  // event handler (e.g. group change: set group + clear category) each see
  // the previous call's changes instead of stale closure-captured params.
  const searchParamsRef = useRef(searchParams)
  searchParamsRef.current = searchParams

  // Initialize filters from URL params
  const filters: ListingFilters = useMemo(() => {
    return {
      query: searchParams.get('q') || '',
      category: searchParams.get('category') || '',
      group: searchParams.get('group') || '',
      location: searchParams.get('location') || '',
      priceMin: searchParams.get('priceMin') || '',
      priceMax: searchParams.get('priceMax') || '',
      condition: searchParams.get('condition') || '',
      saleType: searchParams.get('saleType') || '',
      shipping: searchParams.get('shipping') || '',
      sortBy: (searchParams.get('sort') as SortOption) || 'newest',
      make: searchParams.get('make') || '',
      model: searchParams.get('model') || '',
      yearMin: searchParams.get('yearMin') || '',
      yearMax: searchParams.get('yearMax') || '',
      transmission: searchParams.get('transmission') || '',
      bedrooms: searchParams.get('bedrooms') || '',
      bathrooms: searchParams.get('bathrooms') || '',
    }
  }, [searchParams])

  // Set a single filter
  const setFilter = useCallback(
    (key: keyof ListingFilters, value: string) => {
      const newParams = new URLSearchParams(searchParamsRef.current)
      const paramKey = key === 'sortBy' ? 'sort' : key === 'query' ? 'q' : key
      if (value) {
        newParams.set(paramKey, value)
      } else {
        newParams.delete(paramKey)
      }
      searchParamsRef.current = newParams
      setSearchParams(newParams, { replace: true })
    },
    [setSearchParams]
  )

  // Set multiple filters at once
  const setFilters = useCallback(
    (newFilters: Partial<ListingFilters>) => {
      const newParams = new URLSearchParams(searchParamsRef.current)

      Object.entries(newFilters).forEach(([key, value]) => {
        const paramKey = key === 'sortBy' ? 'sort' : key === 'query' ? 'q' : key
        if (value) {
          newParams.set(paramKey, value)
        } else {
          newParams.delete(paramKey)
        }
      })

      searchParamsRef.current = newParams
      setSearchParams(newParams, { replace: true })
    },
    [setSearchParams]
  )

  // Clear all filters
  const clearFilters = useCallback(() => {
    searchParamsRef.current = new URLSearchParams()
    setSearchParams({}, { replace: true })
  }, [setSearchParams])

  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    return Object.entries(filters).some(([key, value]) => {
      if (key === 'sortBy') return value !== 'newest'
      return !!value
    })
  }, [filters])

  // Get active filter count (excluding sort)
  const activeFilterCount = useMemo(() => {
    return Object.entries(filters).filter(([key, value]) => {
      if (key === 'sortBy') return false
      return !!value
    }).length
  }, [filters])

  return {
    filters,
    setFilter,
    setFilters,
    clearFilters,
    hasActiveFilters,
    activeFilterCount,
  }
}
